import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {AlertTriangle, Pencil, ShoppingCart} from 'lucide-react';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {
  formatUsd,
  getPlan,
  STEP,
  unresolvedProductSteps,
} from '@client/pages/public/enroll/enrollModel';
import {COVERAGE_TIER_LABELS} from '@client/pages/public/enroll/types';
import type {ProductConfig} from '@client/pages/public/enroll/types';
import {DeclineMedicalDialog} from '@client/pages/public/enroll/components/DeclineMedicalDialog';
import {SkipProductDialog} from '@client/pages/public/enroll/components/SkipProductDialog';
import {WarningBanner} from '@client/pages/public/enroll/components/WarningBanner';
import {sitePayPeriod} from '@common/enrollmentSite/payPeriod';

/** The step a product is edited on — its own step, or the shared Protection step for inline products. */
function editStep(product: ProductConfig): string {
  if (product.inlineWith === 'protection') return STEP.Protection;
  return product.inlineWith ?? product.category;
}

/**
 * Read-only recap of every product selection and the per-pay-period total before checkout. Gated products
 * (medical/dental/vision) the user hasn't resolved render an action-needed row and block Continue.
 */
export function ReviewStep() {
  const {group, getTier, selections, total, declineProduct} = useEnrollSteps();
  const payPeriod = sitePayPeriod(group);
  const navigate = useNavigate();
  const [declineMedicalOpen, setDeclineMedicalOpen] = useState(false);
  const [productToSkip, setProductToSkip] = useState<ProductConfig | null>(null);

  const unresolved = unresolvedProductSteps(group, selections);

  const goToStep = (product: ProductConfig) =>
    navigate(`/${group.slug}/${editStep(product)}`);

  const handleDecline = (product: ProductConfig) => {
    if (product.category === STEP.Medical) {
      setDeclineMedicalOpen(true);
    } else if (product.declineStyle === 'skipped') {
      setProductToSkip(product);
    } else {
      declineProduct(product.category);
    }
  };

  return (
    <div className='space-y-6'>
      <div className={`space-y-2 ${POSTHOG_UNMASK_CLASS}`}>
        <h1 className='flex items-center gap-2 text-3xl font-bold text-[var(--enroll-ink)]'>
          <ShoppingCart className='h-7 w-7 text-[var(--enroll-primary)]' />
          Review your benefits
        </h1>
        <p className='text-neutral-600'>
          Confirm the plans below, then continue to checkout to enter your info and any dependents.
        </p>
      </div>

      <div className='overflow-hidden rounded-xl border border-neutral-200 bg-white'>
        <ul className='divide-y divide-neutral-200'>
          {group.products.map((product) => {
            const selection = selections[product.category];
            const plan = selection?.planId ? getPlan(group, selection.planId) : undefined;
            const tier = getTier(product.category);

            const isUnresolved = unresolved.includes(product.category);
            // Opt-in products (Extra Protection, AXIS) with no choice are omitted, like the cart rail.
            if (!plan && !selection?.declined && !isUnresolved) return null;

            if (isUnresolved) {
              return (
                <li
                  key={product.category}
                  className='flex items-center justify-between gap-4 px-5 py-4'
                >
                  <div className='min-w-0'>
                    <p className='text-[11px] font-bold uppercase tracking-wide text-neutral-400'>
                      {product.label}
                    </p>
                    <p className='flex items-center gap-1.5 font-semibold text-amber-700'>
                      <AlertTriangle className='h-4 w-4' />
                      Not yet selected
                    </p>
                  </div>
                  <div className='flex shrink-0 items-center gap-2'>
                    <button
                      type='button'
                      onClick={() => goToStep(product)}
                      className='inline-flex h-9 items-center rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50'
                    >
                      View {product.label.toLowerCase()} plans
                    </button>
                    <button
                      type='button'
                      onClick={() => handleDecline(product)}
                      className='inline-flex h-9 items-center rounded-lg border border-red-300 px-3 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50'
                    >
                      Decline coverage
                    </button>
                  </div>
                </li>
              );
            }

            const showTier = plan && product.tierSelectable !== false;

            return (
              <li
                key={product.category}
                className='flex items-center justify-between gap-4 px-5 py-4'
              >
                <div className='min-w-0'>
                  <p className='text-[11px] font-bold uppercase tracking-wide text-neutral-400'>
                    {product.label}
                  </p>
                  <p
                    className={
                      selection?.declined
                        ? 'font-semibold text-red-600'
                        : 'font-semibold text-neutral-900'
                    }
                  >
                    {plan
                      ? plan.name
                      : selection?.declined
                        ? product.declineStyle === 'skipped'
                          ? 'Skipped'
                          : 'Declined'
                        : 'No selection'}
                  </p>
                  {showTier && (
                    <p className='text-sm text-neutral-500'>{COVERAGE_TIER_LABELS[tier]}</p>
                  )}
                </div>

                <div className='flex items-center gap-5'>
                  <div className='whitespace-nowrap text-right'>
                    {plan ? (
                      <>
                        <div className='font-semibold text-neutral-900'>
                          {formatUsd(plan.rates[tier])}
                        </div>
                        <div className='text-xs text-neutral-400'>{payPeriod.each}</div>
                      </>
                    ) : (
                      <span className='text-neutral-400'>—</span>
                    )}
                  </div>
                  <button
                    type='button'
                    onClick={() => goToStep(product)}
                    className='inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-500 transition-colors hover:text-[var(--enroll-accent)]'
                  >
                    <Pencil className='h-3.5 w-3.5' />
                    Edit
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        <div className='flex items-center justify-between border-t border-neutral-200 bg-[var(--enroll-ink)] px-6 py-5 text-white'>
          <span className='text-sm font-medium'>Estimated {payPeriod.total.toLowerCase()}</span>
          <span className='text-2xl font-bold'>
            {formatUsd(total)}
            <span className='text-sm font-normal opacity-70'>{payPeriod.priceSuffix}</span>
          </span>
        </div>
      </div>

      {unresolved.length > 0 && (
        <WarningBanner>
          <span className='font-semibold'>
            {unresolved.length} {unresolved.length === 1 ? 'selection is' : 'selections are'}{' '}
            missing.
          </span>{' '}
          Select a plan or decline coverage to continue.
        </WarningBanner>
      )}

      <DeclineMedicalDialog
        open={declineMedicalOpen}
        onOpenChange={setDeclineMedicalOpen}
        onConfirm={() => {
          declineProduct(STEP.Medical);
          setDeclineMedicalOpen(false);
        }}
      />

      {productToSkip && (
        <SkipProductDialog
          open
          onOpenChange={(open) => {
            if (!open) setProductToSkip(null);
          }}
          productLabel={productToSkip.label}
          onConfirm={() => {
            declineProduct(productToSkip.category);
            setProductToSkip(null);
          }}
        />
      )}
    </div>
  );
}
